import { useEffect, useState } from 'react';
import { Plus, Building2, MoreHorizontal, Pencil, TrendingUp, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  PageHeader,
  TableFilters,
  DataTable,
  Column,
  StatusBadge,
  ConfirmDialog,
} from '@/components/common';
import { suppliersApi, Supplier, CreateSupplierDto, QuerySupplierParams } from '@/api/services';
import { getErrorMessage } from '@/api/client';

interface SupplierFormData {
  name: string;
  code: string;
  contactName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  rating: number;
  isActive: boolean;
}

const emptyFormData: SupplierFormData = {
  name: '',
  code: '',
  contactName: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  rating: 3,
  isActive: true,
};

export default function Suppliers() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; supplier?: Supplier }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [editingSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState<SupplierFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);

  const fetchSuppliers = async (params?: QuerySupplierParams) => {
    try {
      setLoading(true);
      const response = await suppliersApi.getAll(params);
      setSuppliers(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params: QuerySupplierParams = {
      search: searchQuery || undefined,
    };
    if (statusFilter) {
      params.isActive = statusFilter === 'active';
    }
    fetchSuppliers(params);
  }, [searchQuery, statusFilter]);

  const handleDelete = async () => {
    if (!deleteDialog.supplier) return;

    try {
      setDeleting(true);
      await suppliersApi.delete(deleteDialog.supplier.id);
      toast.success('Supplier deactivated successfully');
      setDeleteDialog({ open: false });
      fetchSuppliers();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const openCreateForm = () => {
    navigate('/suppliers/new');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.code) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);

      const payload: CreateSupplierDto = {
        name: formData.name,
        code: formData.code,
        contactName: formData.contactName || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        rating: formData.rating,
        isActive: formData.isActive,
      };

      // Only include address if at least one field is filled
      if (
        formData.street ||
        formData.city ||
        formData.state ||
        formData.country ||
        formData.postalCode
      ) {
        payload.address = {
          street: formData.street || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          country: formData.country || undefined,
          postalCode: formData.postalCode || undefined,
        };
      }

      if (editingSupplier) {
        await suppliersApi.update(editingSupplier.id, payload);
        toast.success('Supplier updated successfully');
      } else {
        await suppliersApi.create(payload);
        toast.success('Supplier created successfully');
      }

      setFormOpen(false);
      fetchSuppliers();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const getPerformanceBadge = (rating?: number) => {
    if (rating === undefined || rating === null) return null;
    const percentage = rating * 20;
    if (percentage >= 90)
      return <Badge className="bg-emerald-100 text-emerald-700">Excellent</Badge>;
    if (percentage >= 75) return <Badge className="bg-blue-100 text-blue-700">Good</Badge>;
    if (percentage >= 60) return <Badge className="bg-amber-100 text-amber-700">Average</Badge>;
    return <Badge className="bg-red-100 text-red-700">Needs Improvement</Badge>;
  };

  const getRatingLabel = (rating: number) => {
    if (rating >= 4.5) return 'Excellent';
    if (rating >= 3.5) return 'Good';
    if (rating >= 2.5) return 'Average';
    if (rating >= 1.5) return 'Below Average';
    return 'Poor';
  };

  const columns: Column<Supplier>[] = [
    {
      key: 'name',
      linkTo: (supplier) => `/suppliers/${supplier.id}`,
      header: 'Supplier',
      sortable: true,
      render: (supplier) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <Building2 className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{supplier.name}</div>
            <div className="text-xs leading-4 text-muted-foreground">{supplier.code}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (supplier) => (
        <div className="text-sm">
          <div>{supplier.contactName || '-'}</div>
          <div className="text-muted-foreground">{supplier.email || '-'}</div>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (supplier) => (
        <span className="text-muted-foreground">
          {supplier.address?.city && supplier.address?.country
            ? `${supplier.address.city}, ${supplier.address.country}`
            : '-'}
        </span>
      ),
    },
    {
      key: 'performance',
      header: 'Rating',
      render: (supplier) => (
        <div className="flex items-center gap-2">
          {getPerformanceBadge(supplier.rating)}
          {supplier.rating !== undefined && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              {supplier.rating}/5
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'purchaseOrders',
      header: 'POs',
      render: (supplier) => <Badge variant="outline">{supplier._count?.purchaseOrders || 0}</Badge>,
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (supplier) => <StatusBadge status={supplier.isActive ? 'active' : 'inactive'} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (supplier) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/suppliers/${supplier.id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, supplier })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Deactivate
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Suppliers"
        description="Vendors under contract, with contact details, delivery performance, and open orders."
        actions={
          <Button onClick={openCreateForm}>
            <Plus className="mr-2 h-4 w-4" />
            Add Supplier
          </Button>
        }
      />

      <DataTable
        toolbar={
          <TableFilters
            search={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search suppliers..."
            filters={[
              {
                id: 'status',
                label: 'Status',
                value: statusFilter,
                options: [
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                ],
                onChange: setStatusFilter,
                placeholder: 'Status',
              },
            ]}
          />
        }
        data={suppliers}
        columns={columns}
        loading={loading}
        onRowClick={(supplier) => navigate(`/suppliers/${supplier.id}`)}
        emptyTitle="No suppliers found"
        emptyDescription="Get started by adding your first supplier."
        emptyAction={
          <Button onClick={openCreateForm}>
            <Plus className="mr-2 h-4 w-4" />
            Add Supplier
          </Button>
        }
        pagination
        exportable
        columnToggle
      />

      {/* Create/Edit Supplier Sheet */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editingSupplier ? 'Edit Supplier' : 'Add Supplier'}</SheetTitle>
            <SheetDescription>
              {editingSupplier
                ? 'Update the supplier details below.'
                : 'Fill in the details to create a new supplier.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Basic Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Acme Corp"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    placeholder="ACME-001"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Active Status</Label>
                  <p className="text-sm text-muted-foreground">
                    {formData.isActive ? 'Supplier is active' : 'Supplier is inactive'}
                  </p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Contact</h4>
              <div className="space-y-2">
                <Label htmlFor="contactName">Contact Name</Label>
                <Input
                  id="contactName"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@acme.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 555-123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Address</h4>
              <div className="space-y-2">
                <Label htmlFor="street">Street</Label>
                <Input
                  id="street"
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  placeholder="123 Main St"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Houston"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State/Province</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="TX"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="USA"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postalCode">Postal Code</Label>
                  <Input
                    id="postalCode"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    placeholder="77001"
                  />
                </div>
              </div>
            </div>

            {/* Rating */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground">Performance Rating</h4>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <Label>Rating: {formData.rating}/5</Label>
                  <span className="text-sm text-muted-foreground">
                    {getRatingLabel(formData.rating)}
                  </span>
                </div>
                <Slider
                  value={[formData.rating]}
                  onValueChange={(value) => setFormData({ ...formData, rating: value[0] })}
                  min={1}
                  max={5}
                  step={0.5}
                  className="w-full"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingSupplier ? 'Update Supplier' : 'Create Supplier'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open })}
        title="Deactivate Supplier"
        description={`Are you sure you want to deactivate "${deleteDialog.supplier?.name}"? This supplier will no longer be available for new orders.`}
        confirmLabel="Deactivate"
        onConfirm={handleDelete}
        variant="destructive"
        loading={deleting}
      />
    </div>
  );
}
